// app/providers.tsx
"use client"

import { usePathname, useSearchParams } from "next/navigation"
import posthog from "posthog-js"
import { usePostHog } from "posthog-js/react"
import { PostHogProvider as PHProvider } from "posthog-js/react"
import { Suspense, useEffect } from "react"

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
      person_profiles: "identified_only",
      capture_pageview: false,
      capture_performance: true, // Enable performance monitoring
      enable_recording_console_log: true, // Enable console log capturing for errors
    })

    // Add global error handler
    window.addEventListener("error", (event) => {
      posthog.capture("error", {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error?.toString(),
      })
    })

    // Add unhandled promise rejection handler
    window.addEventListener("unhandledrejection", (event) => {
      posthog.capture("unhandled_promise_rejection", {
        reason: event.reason?.toString(),
      })
    })
  }, [])

  return posthog.__loaded ? (
    <PHProvider client={posthog}>
      <SuspendedPostHogPageView />
      {children}
    </PHProvider>
  ) : (
    <>{children}</>
  )
}

function PostHogPageView() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const posthog = usePostHog()

  // Track only 404 pages
  useEffect(() => {
    if (pathname && posthog) {
      let url = window.origin + pathname
      if (searchParams.toString()) {
        url = url + "?" + searchParams.toString()
      }

      // Only capture 404 pages
      if (document.title.includes("404") || pathname.includes("404")) {
        posthog.capture("404_pageview", {
          $current_url: url,
          path: pathname,
          search_params: searchParams.toString(),
        })
      }
    }
  }, [pathname, searchParams, posthog])

  return null
}

// Wrap PostHogPageView in Suspense to avoid the useSearchParams usage above
// from de-opting the whole app into client-side rendering
// See: https://nextjs.org/docs/messages/deopted-into-client-rendering
function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  )
}
